import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Icon, type IconName } from "./Workspace.tsx";

type Workspace = "mgr" | "sales";
const WORKSPACES: ReadonlyArray<{ value: Workspace; label: string; description: string; icon: IconName }> = [
  { value: "mgr", label: "管理工作台", description: "团队经营与客户管理", icon: "home" },
  { value: "sales", label: "销售工作台", description: "客户跟进与今日行动", icon: "users" },
];

export function WorkspaceSwitcher(props: { value: Workspace; active: boolean; onChange: (value: Workspace) => void }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const options = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();
  const selectedIndex = WORKSPACES.findIndex((workspace) => workspace.value === props.value);
  const selected = WORKSPACES[selectedIndex]!;

  useEffect(() => {
    if (!props.active) setOpen(false);
  }, [props.active]);

  useEffect(() => {
    if (!open || !props.active) return;
    options.current[selectedIndex]?.focus();
    const dismiss = (event: PointerEvent): void => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open, props.active, selectedIndex]);

  const close = (): void => { setOpen(false); trigger.current?.focus(); };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape" && open) {
      event.preventDefault(); event.stopPropagation(); close();
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) { setOpen(true); return; }
      const focused = options.current.findIndex((option) => option === document.activeElement);
      const direction = event.key === "ArrowDown" ? 1 : -1;
      options.current[((focused < 0 ? selectedIndex : focused) + direction + WORKSPACES.length) % WORKSPACES.length]?.focus();
    } else if (open && (event.key === "Home" || event.key === "End")) {
      event.preventDefault();
      options.current[event.key === "Home" ? 0 : WORKSPACES.length - 1]?.focus();
    }
  };

  return <div ref={root} className="workspace-select" onKeyDown={handleKeyDown} onBlur={(event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }}>
    <button ref={trigger} type="button" className={`workspace-trigger ${open ? "is-open" : ""}`}
      aria-label={`切换工作台，当前为${selected.label}`} aria-haspopup="menu" aria-expanded={open && props.active}
      aria-controls={open && props.active ? menuId : undefined} onClick={() => setOpen(!open)}>
      <span className="workspace-name">{selected.label}</span>
      <svg className="workspace-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
    </button>
    {open && props.active && <div id={menuId} className="workspace-menu" role="menu" aria-label="切换工作台">
      <div className="workspace-menu-caption" aria-hidden="true">选择工作台</div>
      {WORKSPACES.map((workspace, index) => <button key={workspace.value} ref={(element) => { options.current[index] = element; }}
        type="button" role="menuitemradio" aria-checked={workspace.value === props.value}
        aria-label={workspace.label} aria-describedby={`${menuId}-${workspace.value}`} tabIndex={-1}
        className={`workspace-option ${workspace.value === props.value ? "is-selected" : ""}`} onClick={() => {
          close();
          if (workspace.value !== props.value) props.onChange(workspace.value);
        }}>
        <span className="workspace-option-icon"><Icon name={workspace.icon} size={17} /></span>
        <span className="workspace-option-copy"><b>{workspace.label}</b><span id={`${menuId}-${workspace.value}`}>{workspace.description}</span></span>
        {workspace.value === props.value && <span className="workspace-option-check"><Icon name="check" size={15} /></span>}
      </button>)}
    </div>}
  </div>;
}
